import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
  FormField,
} from '@/components/ui/form';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { CalendarDays, CalendarRange } from 'lucide-react';
import React from 'react';
import { Matcher } from 'react-day-picker';
import { FieldPathByValue, FieldValues } from 'react-hook-form';

import { FieldProps } from './shared';

export function DatePickerField<
  T extends FieldValues,
  P extends FieldPathByValue<T, Matcher | null | undefined>,
>({
  control,
  name,
  rules,
  label,
  placeholder,
  description,
  className,
  mode = 'single',
  dateFormat = 'dd-MM-yyyy',
  ...props
}: FieldProps<T, P> &
  Omit<React.ComponentPropsWithoutRef<typeof Calendar>, 'selected'> & {
    placeholder?: string;
    dateFormat?: string;
  }) {
  return (
    <FormField
      control={control}
      name={name}
      rules={rules}
      render={({ field: { ref, value, onChange, ...field } }) => (
        <FormItem className={cn('grow flex flex-col', className)}>
          <FormLabel>{label}</FormLabel>
          <Popover>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  ref={ref}
                  variant="outline"
                  className={cn(
                    'w-auto pl-3 text-left font-normal',
                    !value && 'text-muted-foreground'
                  )}
                >
                  {value ? (
                    format(value, dateFormat)
                  ) : (
                    <span className="truncate">
                      {placeholder ?? 'Pick a date'}
                    </span>
                  )}
                  <span className="ml-auto opacity-50">
                    {mode === 'range' ? (
                      <CalendarRange size={20} />
                    ) : (
                      <CalendarDays size={20} />
                    )}
                  </span>
                </Button>
              </FormControl>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode={mode}
                selected={value}
                onSelect={onChange}
                captionLayout="dropdown-buttons"
                initialFocus
                {...props}
                {...field}
              />
            </PopoverContent>
          </Popover>
          <FormDescription>{description?.(value)}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
